/**
 * Structured API errors.
 *
 * `authenticatedRequest` used to throw `new Error("API error 409: <body>")`,
 * so every caller showed the raw JSON of a refusal. The recovery routes answer
 * with specific 409s (illegal transition, concurrent change, not in
 * maintenance, SideroLink not live) and the JBS install with a structured
 * `site_not_ready` body; showing those needs the status and the parsed body.
 *
 * `ApiError` keeps the old message format, so existing `message.startsWith(
 * 'API error 404')` checks still work. `parseApiError` also accepts a plain
 * Error carrying that format (tests mock errors that way).
 */

export class ApiError extends Error {
  readonly status: number;
  /** Parsed JSON body, or the raw text when it wasn't JSON. */
  readonly body: unknown;

  constructor(status: number, rawBody: string) {
    super(`API error ${status}: ${rawBody}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = parseBody(rawBody);
  }
}

function parseBody(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

export interface ParsedApiError {
  /** HTTP status, when the error came from an API response. */
  status: number | null;
  /** The machine-readable `error` field of a JSON body (e.g. `site_not_ready`). */
  code: string | null;
  /** The best human-readable message available. */
  message: string;
  body: unknown;
}

const MESSAGE_RE = /^API error (\d{3}): ([\s\S]*)$/;

function statusAndBody(err: unknown): { status: number; body: unknown } | null {
  if (err instanceof ApiError) return { status: err.status, body: err.body };
  if (err instanceof Error) {
    const m = MESSAGE_RE.exec(err.message);
    if (m) return { status: Number(m[1]), body: parseBody(m[2]) };
  }
  return null;
}

export function parseApiError(err: unknown): ParsedApiError {
  const parsed = statusAndBody(err);
  if (!parsed) {
    return { status: null, code: null, message: err instanceof Error ? err.message : String(err), body: null };
  }
  const { status, body } = parsed;

  if (body && typeof body === 'object') {
    const b = body as { error?: unknown; message?: unknown };
    const code = typeof b.error === 'string' ? b.error : null;
    // `{ error: 'site_not_ready', message: '…' }` puts the prose in `message`;
    // most routes put it in `error` itself.
    const message = typeof b.message === 'string' && b.message
      ? b.message
      : code ?? `Request failed (${status})`;
    return { status, code, message, body };
  }
  const text = typeof body === 'string' && body.trim() ? body.trim() : `Request failed (${status})`;
  return { status, code: null, message: text, body };
}
